import api from 'src/api';
import {
  determineSelectedManagementOption,
  determineSelectedOwnerRepOption,
  isNullUndefinedOrEmptyObject,
} from '../../../common/utils';
import { mapUpdateFlowConfigToFlowSteps } from '../flow/flowThunks';
import {
  resetDocumentUploadProgressStatus,
  setDocumentUploadCompleted,
  setDocumentUploadError,
  setDocumentUploadWorking,
  setInitiatingUploadFlowComplete,
  setInitiatingUploadFlowInProgress,
  setUpdateApplicationNumber,
  setUpdateLicenseCompleted,
  setUpdateLicenseError,
  setUpdateLicenseInProgress,
  updatePermitData,
} from './updateSlice';
import { AppThunk } from 'src/store/store';
import { ExpirationApplicationQuestion } from 'src/types/configuration';
import { ApplicationData, PropertyDetails } from 'src/types/flow';
import { Person } from 'src/types/person';

export const initiateUpdateFlow = (
  applicationNumber: string,
  originPath: string,
  onSuccess: () => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    try {
      dispatch(setInitiatingUploadFlowInProgress());
      const { data } = await api.getLicenseAsync(getState(), applicationNumber);
      // make sure the data returned has the correct properties
      if (data.registrantType) {
        let isResidential = false;
        try {
          const lookupResult = await api.lookupAPNAddress(data.apn);
          if (lookupResult) {
            isResidential = lookupResult?.parcelSpecificInfo?.residential;
          }
        } catch (error) {
          // Nothing to do here - assume this is not residential
        }

        // get supplementalconfig for each flow
        const supplementalQuestionsConfigApply =
          getState()?.config?.settings?.express?.registration?.application
            ?.questions;
        const supplementalQuestionsConfigUpdate =
          getState()?.config?.settings?.express?.registration?.update
            ?.questions;
        const supplementalQuestionsConfigRenew =
          getState()?.config?.settings?.express?.registration?.renew?.questions;
        const supplementalQuestionsConfigAdminUpdate =
          getState()?.config?.settings?.express?.registration?.admin_update
            ?.questions;
        const supplementalQuestions = data?.misc?.supplementalQuestions;

        const hasAnswer = (questionKey: string) => {
          return (
            supplementalQuestions?.[questionKey]?.answer !== null &&
            supplementalQuestions?.[questionKey]?.answer !== undefined
          );
        };
        // combine configs
        // We need to do this becuase when we call /update we need to send through the supplemental questions, otherwise they are cleared
        // put update flow last since it should be the highest priority
        // the pages (property details, confirmation) where this is used will only display the appropriate questions
        // Don't include questions from other flows if they do not have an answer (if we include them api request will fail)
        const filteredSupplementalQuestionsConfig = [
          ...(supplementalQuestionsConfigApply || []).filter((q) =>
            hasAnswer(q.key as string)
          ),
          ...(supplementalQuestionsConfigRenew || []).filter((q) =>
            hasAnswer(q.key as string)
          ),
          ...(supplementalQuestionsConfigAdminUpdate || []).filter((q) =>
            hasAnswer(q.key as string)
          ),
          ...(supplementalQuestionsConfigUpdate || []),
        ];

        const uniqueSupplementalQuestions =
          filteredSupplementalQuestionsConfig.reduce(
            (
              acc: ExpirationApplicationQuestion[],
              current: ExpirationApplicationQuestion
            ) => {
              const existingIndex = acc.findIndex(
                (item) => item.key === current.key
              );
              // If key does not exist add it else replace existing value
              if (existingIndex === -1) {
                acc.push(current);
              } else {
                acc[existingIndex] = current;
              }
              return acc;
            },
            []
          );

        const supplementalQuestionResults = data.misc?.supplementalQuestions;

        const propertyDetails: PropertyDetails = { supplementalQuestions: {} };

        if (
          !isNullUndefinedOrEmptyObject(filteredSupplementalQuestionsConfig)
        ) {
          uniqueSupplementalQuestions.forEach((question) => {
            const key = question.key as string;

            // Populate supplemental questions object
            (propertyDetails.supplementalQuestions as Record<string, unknown>)[
              key
            ] = {
              question: question.title,
              questionSubtext: question.description,
              ref: key,
              answer: supplementalQuestionResults?.[key]?.answer,
            };

            // Populate top level fields
            propertyDetails[key] = supplementalQuestionResults?.[key]?.answer;
          });
        }

        const selectedManagementOption = determineSelectedManagementOption(
          data.people
        );
        const selectedOwnerRepOption = determineSelectedOwnerRepOption(
          data.people
        );

        const userEmail = getState().auth?.cognitoEmailAddress;
        const ownerArr = Array.isArray(data.people.owner)
          ? data.people.owner
          : [data.people.owner];
        const isOwner = ownerArr?.some(
          (item: Person) => item.email === userEmail
        );
        const parsedData: ApplicationData = {
          property: {
            propertyaddress: data.address,
            propertyid: data.apn,
            unitnumber: data.unitNumber,
            rentalstructure: data.rentalStructure,
            isResidential,
          },
          registrant: {
            registrantType: data.registrantType,
            owner: Array.isArray(data.people.owner)
              ? data.people.owner
              : [data.people.owner], // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object
            registrant: data.people.registrant,
          },
          contacts: {
            selectedManagementOption,
            selectedOwnerRepOption,
            propertyManager: data.people?.propertyManager,
            localContact: data.people?.localContact,
          },
          propertyDetails,
          tenant: data.people?.tenant,
        };
        dispatch(setUpdateApplicationNumber(applicationNumber));
        dispatch(updatePermitData(parsedData));
        dispatch(
          mapUpdateFlowConfigToFlowSteps(onSuccess, originPath, isOwner)
        );
        dispatch(setInitiatingUploadFlowComplete(''));
      } else {
        dispatch(
          setInitiatingUploadFlowComplete(
            'Unable to initiate update flow due to missing data. Please contact Deckard.'
          )
        );
      }
    } catch (err) {
      dispatch(
        setInitiatingUploadFlowComplete(
          'Something went wrong while fetching required data.'
        )
      );
    }
  };
};

export const updateLicense = (
  onCompletionCallback: (wasSuccesfull: boolean) => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    let wasSuccesfull = true;
    let hasDocumentUploadError = false;

    try {
      dispatch(setUpdateLicenseInProgress());

      // GET data from Redux
      const applicationNumber = getState().update?.applicationNumber;
      const { documents } = getState().update
        ?.applicationData as ApplicationData;

      // UPDATE LICENSE
      await api.updateLicense(getState());

      // UPLOAD DOCUMENTS
      if (documents) {
        try {
          // Each type of document may have zero or more associated files
          // but requires only one upload token per type
          // So we will store the configuration details for each document type in an array
          const uploadConfiguration = [];

          // Get a list of the document types (documents are stored in Redux with the document type as their key)
          const documentTypes = Object.keys(documents);
          // Upload the documents using the new license number
          for (const documentType in documentTypes) {
            const docTypeName = documentTypes[documentType];
            // Get an upload token for each document type
            const { uploadToken } = await api.fetchDocumentUploadToken(
              applicationNumber,
              docTypeName,
              {},
              getState()
            );

            // Store the token and associated files in the array as its own config object
            uploadConfiguration.push({
              document: documentType,
              uploadToken: uploadToken,
              files: documents[docTypeName],
            });
          }

          if (
            !uploadConfiguration ||
            uploadConfiguration.length === 0 ||
            uploadConfiguration.filter(
              (config) => config.files.attachedFiles.length === 0
            ).length === uploadConfiguration.length
          ) {
            // If there is no uploadConfiguration, it's empty or it only contains configs with no associated files, then we're done.
            dispatch(setDocumentUploadCompleted(true));
          } else {
            dispatch(setDocumentUploadWorking(true));
            // Do the upload
            for (const uploadConfig of uploadConfiguration) {
              const { files } = uploadConfig;
              const { attachedFiles } = files;
              for (const documentFile of attachedFiles) {
                const { blobFile } = documentFile;
                await api.uploadDocument({
                  uploadToken: uploadConfig.uploadToken,
                  file: blobFile,
                });
              }
            }
          }
        } catch (err) {
          wasSuccesfull = false;
          hasDocumentUploadError = true;
        }

        if (hasDocumentUploadError) {
          dispatch(
            setDocumentUploadError(
              'An unexpected error occurred while attempting to upload documents.'
            )
          );
        }
      }
    } catch (err) {
      dispatch(resetDocumentUploadProgressStatus());
      dispatch(
        setUpdateLicenseError(
          'An unexpected error occurred while attempting to update.'
        )
      );
      wasSuccesfull = false;
    } finally {
      dispatch(setDocumentUploadCompleted(true));
      dispatch(setUpdateLicenseCompleted());
      onCompletionCallback(wasSuccesfull);
    }
  };
};
